import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { ShiftComparison as ShiftComparisonType } from '../../types/stats';

interface ShiftComparisonProps {
  data: ShiftComparisonType[];
}

export const ShiftComparison = ({ data }: ShiftComparisonProps) => {
  const option = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Normalize and aggregate shifts (handle case variations and duplicates)
    const shiftMap = new Map<string, typeof data[0]>();
    data.forEach((item) => {
      const normalizedShift = item.shift.trim().toLowerCase();
      const existing = shiftMap.get(normalizedShift);
      if (existing) {
        // Aggregate data for duplicate shifts
        existing.total += item.total;
        existing.accepted += item.accepted;
        existing.rejected += item.rejected;
        // Merge top defect types
        item.topDefectTypes.forEach((defect) => {
          const existingDefect = existing.topDefectTypes.find((d) => d.type === defect.type);
          if (existingDefect) {
            existingDefect.count += defect.count;
          } else {
            existing.topDefectTypes.push({ ...defect });
          }
        });
        // Recalculate defect rate
        existing.defectRate = existing.total > 0 ? (existing.rejected / existing.total) * 100 : 0;
      } else {
        // Use the original shift name (capitalize first letter)
        const displayName = item.shift.trim().charAt(0).toUpperCase() + item.shift.trim().slice(1).toLowerCase();
        shiftMap.set(normalizedShift, {
          ...item,
          shift: displayName,
        });
      }
    });

    // Convert map to array and sort
    const aggregatedData = Array.from(shiftMap.values()).sort((a, b) => {
      // Sort: Day first, then Night, then others alphabetically
      const order: Record<string, number> = { day: 1, night: 2 };
      const aOrder = order[a.shift.toLowerCase()] || 99;
      const bOrder = order[b.shift.toLowerCase()] || 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.shift.localeCompare(b.shift);
    });

    const shifts = aggregatedData.map((item) => item.shift);
    const accepted = aggregatedData.map((item) => item.accepted);
    const rejected = aggregatedData.map((item) => item.rejected);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const shift = aggregatedData[index];
          let tooltip = `
            <strong>${shift.shift} Shift</strong><br/>
            Total: ${shift.total}<br/>
            Accepted: ${shift.accepted}<br/>
            Rejected: ${shift.rejected}
          `;
          if (shift.topDefectTypes.length > 0) {
            tooltip += '<br/><br/>Top Defect Types:<br/>';
            shift.topDefectTypes.forEach((defect) => {
              tooltip += `• ${defect.type}: ${defect.count}<br/>`;
            });
          }
          return tooltip;
        },
      },
      legend: {
        data: ['Accepted', 'Rejected'],
        top: 10,
      },
      grid: { left: 80, right: 30, top: 60, bottom: 60 },
      xAxis: {
        type: 'category',
        data: shifts,
      },
      yAxis: {
        type: 'value',
        name: 'Number of Welds',
        position: 'left',
      },
      series: [
        {
          name: 'Accepted',
          type: 'bar',
          data: accepted,
          itemStyle: { color: '#22c55e' },
          stack: 'welds',
        },
        {
          name: 'Rejected',
          type: 'bar',
          data: rejected,
          itemStyle: { color: '#ef4444' },
          stack: 'welds',
        },
      ],
    } as echarts.EChartsOption;
  }, [data]);

  if (!option) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No shift comparison data available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Shift Comparison</h3>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Day vs Night Performance</p>
      </div>
      <ReactECharts 
        option={option} 
        style={{ height: 450 }} 
        key="shift-comparison"
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
};

